import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldAlert, X, Lock } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { isYouTube, toYouTubeEmbed, isVimeo, toVimeoEmbed } from "@/lib/videoUrl";
import { toast } from "sonner";

const WATERMARK_POSITIONS = [
  { top: "8%", left: "6%" },
  { top: "8%", right: "6%" },
  { top: "50%", left: "6%", transform: "translateY(-50%)" },
  { top: "50%", right: "6%", transform: "translateY(-50%)" },
  { bottom: "12%", left: "6%" },
  { bottom: "12%", right: "6%" },
];

/**
 * Protected video player with warning modal + dynamic moving watermark + no-download controls.
 * Usage: <VideoPlayer recording={rec} open={open} onOpenChange={setOpen} />
 */
export function VideoPlayer({ recording, open, onOpenChange, playbackPath, refreshPath }) {
  const playbackEndpoint = playbackPath || `/recordings/${recording.id}/playback`;
  const refreshEndpoint = refreshPath || `/recordings/${recording.id}/playback/refresh`;
  const [stage, setStage] = useState("warning"); // warning | loading | playing | error
  const [playbackUrl, setPlaybackUrl] = useState("");
  const [urlType, setUrlType] = useState("internal");
  const [expiresIn, setExpiresIn] = useState(0); // seconds — server-supplied
  const [watermark, setWatermark] = useState(null);
  const [posIndex, setPosIndex] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const videoRef = useRef(null);
  const refreshTimerRef = useRef(null);

  // Reset when opening
  useEffect(() => {
    if (open) {
      setStage("warning");
      setPlaybackUrl("");
      setWatermark(null);
      setErrorMsg("");
    } else if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, [open]);

  // Rotate watermark position every 45s
  useEffect(() => {
    if (stage !== "playing") return;
    const id = setInterval(() => {
      setPosIndex((i) => (i + Math.floor(Math.random() * 5) + 1) % WATERMARK_POSITIONS.length);
    }, 45000);
    return () => clearInterval(id);
  }, [stage]);

  // Silently refresh the signed URL before it expires while the student is watching.
  // Preserves currentTime + paused state — playback is NOT interrupted.
  useEffect(() => {
    if (stage !== "playing" || urlType !== "internal" || !expiresIn) return;
    // Refresh at 70% of TTL; min 30s, max 30min between refreshes
    const refreshAfterSec = Math.min(Math.max(Math.round(expiresIn * 0.7), 30), 30 * 60);

    const scheduleRefresh = () => {
      refreshTimerRef.current = setTimeout(async () => {
        try {
          const { data } = await api.post(refreshEndpoint);
          if (!data?.video_url) return;
          const v = videoRef.current;
          if (v) {
            const wasPaused = v.paused;
            const t = v.currentTime;
            const newAbs = data.url_type === "internal" && data.video_url.startsWith("/api/")
              ? `${process.env.REACT_APP_BACKEND_URL}${data.video_url}`
              : data.video_url;
            // Preload metadata, then seek + restore play state without showing controls reset
            const onLoadedMeta = () => {
              try { v.currentTime = t; } catch { /* ignore */ }
              if (!wasPaused) v.play().catch(() => {});
              v.removeEventListener("loadedmetadata", onLoadedMeta);
            };
            v.addEventListener("loadedmetadata", onLoadedMeta);
            v.src = newAbs;
            v.load();
          }
          setPlaybackUrl(data.video_url);
          if (data.expires_in) setExpiresIn(data.expires_in);
        } catch {
          // Silent fail — next manual interaction will surface auth errors via onError
        } finally {
          scheduleRefresh();
        }
      }, refreshAfterSec * 1000);
    };

    scheduleRefresh();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [stage, urlType, expiresIn, refreshEndpoint]);

  const startPlayback = async () => {
    setStage("loading");
    try {
      const { data } = await api.post(playbackEndpoint);
      setPlaybackUrl(data.video_url);
      setUrlType(data.url_type);
      setExpiresIn(data.expires_in || 0);
      setWatermark(data.watermark);
      setStage("playing");
    } catch (err) {
      const msg = err.response?.data?.detail || "Gagal memuat video. Please cuba lagi.";
      setErrorMsg(typeof msg === "string" ? msg : "Unexpected error.");
      setStage("error");
      toast.error(typeof msg === "string" ? msg : "Gagal memuat video.");
    }
  };

  const close = () => onOpenChange(false);

  const wmStyle = WATERMARK_POSITIONS[posIndex];
  const wmText = watermark
    ? `${watermark.email} | ${watermark.user_id_short} | ${new Date(watermark.timestamp).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" })}`
    : "";

  // Build absolute URL for the protected stream
  const absoluteUrl = urlType === "internal" && playbackUrl.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${playbackUrl}`
    : playbackUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl p-0 overflow-hidden bg-black border-primary/30 sm:rounded-2xl rounded-xl w-[calc(100vw-1rem)] sm:w-[min(56rem,calc(100vw-2rem))] max-h-[100dvh]"
        data-testid="video-player-modal"
        onContextMenu={(e) => e.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Protected Video Player</DialogTitle>
          <DialogDescription>Protected class recording playback</DialogDescription>
        </DialogHeader>
        {stage === "warning" && (
          <div className="p-8 sm:p-10 bg-card text-foreground" data-testid="video-warning">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-4">
              <ShieldAlert className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-xl sm:text-2xl font-heading font-bold text-center">
              Protected Learning Content
            </h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed text-center max-w-xl mx-auto">
              This class recording is provided for personal learning only. Recording, downloading,
              sharing, reselling, or redistributing the content to third parties is strictly prohibited.
            </p>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed text-center max-w-xl mx-auto">
              Every viewing session is protected by user watermarking, device tracking, and activity
              logging to safeguard the content.
            </p>
            <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4 text-xs text-muted-foreground space-y-1.5">
              <p>• Video security session is automatically refreshed</p>
              <p>• Students can watch full recordings while their class access is active</p>
              <p>• Each account is limited to 2 devices</p>
              <p>• Suspicious activity will be detected by admin</p>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={close} className="sm:flex-1" data-testid="video-cancel-btn">
                Cancel
              </Button>
              <Button onClick={startPlayback} className="sm:flex-1" data-testid="video-accept-btn">
                I Understand
              </Button>
            </div>
          </div>
        )}

        {stage === "loading" && (
          <div className="p-16 bg-card flex items-center justify-center">
            <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        )}

        {stage === "error" && (
          <div className="p-10 bg-card text-center" data-testid="video-error">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-destructive/15 border border-destructive/30 flex items-center justify-center mb-4">
              <Lock className="h-7 w-7 text-destructive" />
            </div>
            <h2 className="text-xl font-heading font-bold">Unable to Play Video</h2>
            <p className="mt-2 text-sm text-muted-foreground">{errorMsg}</p>
            <Button onClick={close} className="mt-6">Close</Button>
          </div>
        )}

        {stage === "playing" && (
          <div className="video-player-wrapper select-none" data-testid="video-playing">
            {/* Close button */}
            <button
              onClick={close}
              className="absolute top-3 right-3 z-30 h-9 w-9 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur flex items-center justify-center text-white transition-colors"
              aria-label="Close player"
              data-testid="video-close-btn"
            >
              <X className="h-4 w-4" />
            </button>

            {/* The player itself */}
            {!playbackUrl ? (
              <div className="absolute inset-0 flex items-center justify-center text-center px-6">
                <p className="text-sm text-white/80">Video URL not set.</p>
              </div>
            ) : urlType === "external" && isYouTube(playbackUrl) ? (
              <iframe
                src={toYouTubeEmbed(playbackUrl)}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                allowFullScreen
                title={recording.title || "Class recording"}
                referrerPolicy="strict-origin-when-cross-origin"
              />
            ) : urlType === "external" && isVimeo(playbackUrl) ? (
              <iframe
                src={toVimeoEmbed(playbackUrl) || playbackUrl}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={recording.title || "Class recording"}
              />
            ) : (
              <video
                ref={videoRef}
                src={absoluteUrl}
                controls
                autoPlay
                className="absolute inset-0 w-full h-full"
                controlsList="nodownload noplaybackrate noremoteplayback"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                crossOrigin="anonymous"
              />
            )}

            {/* Dynamic watermark overlay */}
            {watermark && (
              <div
                className="absolute z-20 pointer-events-none transition-all duration-700 ease-out"
                style={wmStyle}
                data-testid="video-watermark"
              >
                <div className="rounded-md bg-black/45 backdrop-blur-[2px] border border-white/15 px-2.5 py-1.5">
                  <p className="text-[11px] sm:text-xs text-white/85 font-mono tracking-wide">
                    {watermark.name}
                  </p>
                  <p className="text-[10px] sm:text-[11px] text-white/65 font-mono">
                    {wmText}
                  </p>
                </div>
              </div>
            )}

            {/* Small corner tag — always visible */}
            <div className="absolute bottom-3 left-3 z-20 pointer-events-none">
              <div className="rounded bg-primary/25 border border-primary/40 px-2 py-0.5 backdrop-blur-sm">
                <p className="text-[10px] text-white/90 font-mono">eLearningSaham · Protected</p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
